import { useEffect, useRef, type DependencyList } from 'react'

// How close to the bottom (px) still counts as "pinned", tolerating fractional
// pixels and the tail end of smooth-scroll animations.
const BOTTOM_THRESHOLD_PX = 80

/**
 * Auto-scroll a container to the bottom whenever `deps` change, but only while the
 * user is pinned to the bottom — once they scroll up to read, new content no longer
 * yanks them down. This is the BeAssistant chat behavior, shared with the task run
 * log viewer.
 *
 * Attach `scrollContainerRef` and `onScroll` to the scrollable element, and render
 * an empty `<div ref={bottomRef} />` as its last child.
 */
export function usePinnedAutoScroll(deps: DependencyList) {
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  // Whether the user is pinned to the bottom. Tracked in a ref (not state) so
  // content updates don't re-render on every scroll tick, and so the effect reads
  // the position from *before* the new content grew the scroll height. Defaults to
  // true so the first render scrolls down.
  const isPinnedToBottomRef = useRef(true)

  useEffect(() => {
    // Only auto-scroll if the bottom was visible when this update arrived;
    // otherwise the user has scrolled up to read and we leave them be.
    if (isPinnedToBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-owned dependency list
  }, deps)

  const onScroll = () => {
    const el = scrollContainerRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    isPinnedToBottomRef.current = distanceFromBottom <= BOTTOM_THRESHOLD_PX
  }

  return { bottomRef, scrollContainerRef, onScroll }
}
